import React, { useEffect, useRef, useState } from 'react';
import { AdCreative, serveAds, detectDevice } from '../../api/ads/ads.api';
import { AdOptionsMenu, getHiddenIds, recordAdClick, useAdImpression } from './ad-tracking';

// Giữ chỗ import cũ (Reels, test) sau khi tách phần dùng chung sang ad-tracking.
export { getHiddenIds, IMPRESSION_MIN_RATIO, IMPRESSION_MIN_MS } from './ad-tracking';

interface NativeAdCardProps {
  categoryId?: string;
  placement?: 'Feed' | 'Reels' | 'Video' | 'Profile';
  creatorId?: string;
  postId?: string;
  variant?: 'card' | 'reel';
  /** Quảng cáo đã lấy sẵn (Reels): không serve lại. */
  initialAd?: AdCreative;
}

/**
 * Thẻ quảng cáo Native chèn vào feed — tự lấy 1 quảng cáo hợp lệ (serve), render như 1 bài "Được tài trợ",
 * ghi HIỂN THỊ khi vào khung nhìn, ghi CLICK khi bấm. Người xem có thể BÁO CÁO hoặc ẨN quảng cáo.
 */
const NativeAdCard: React.FC<NativeAdCardProps> = ({
  categoryId,
  placement = 'Feed',
  creatorId,
  postId,
  variant = 'card',
  initialAd,
}) => {
  const [ad, setAd] = useState<AdCreative | null>(initialAd ?? null);
  const [hidden, setHidden] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialAd) return;
    let alive = true;
    serveAds({
      categoryId,
      device: detectDevice(),
      placement,
      count: 1,
      ...(creatorId ? { creatorId } : {}),
      ...(postId ? { postId } : {}),
    })
      .then((res) => {
        if (!alive || !res?.length) return;
        if (getHiddenIds().includes(res[0].campaignId)) return; // đã ẩn thì bỏ qua
        setAd(res[0]);
      })
      .catch(() => {
        /* im lặng: không có ads thì thôi */
      });
    return () => {
      alive = false;
    };
  }, [categoryId, placement, creatorId, postId, initialAd]);

  // Ghi HIỂN THỊ 1 lần: thẻ hiện ≥ 50% LIÊN TỤC ≥ 1 giây và tab đang hiển thị.
  useAdImpression(ad, ref);

  const onClick = () => {
    if (ad) recordAdClick(ad);
  };

  if (!ad || hidden) return null;

  return (
    <div
      ref={ref}
      data-testid="creator-ad"
      className={
        variant === 'reel'
          ? 'relative h-full w-full flex flex-col justify-center overflow-y-auto bg-white dark:bg-dark2 p-6 cursor-pointer'
          : 'box relative border border-[#DEE0E6] dark:border-slate-700 bg-white dark:bg-dark2 rounded-xl p-4 cursor-pointer'
      }
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide text-[#657384] dark:text-slate-400">
          {ad.sponsored || 'Được tài trợ'}
        </span>
        {/* Nhãn "Quảng cáo" và menu không phải phần bấm của quảng cáo: chặn để không bị tính là click có tính phí. */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-[#657384] dark:text-slate-300">
            Quảng cáo
          </span>
          <AdOptionsMenu ad={ad} onHidden={() => setHidden(true)} />
        </div>
      </div>

      {ad.title ? (
        <h3 className="font-semibold text-[15px] ui-text mb-1">{ad.title}</h3>
      ) : null}
      {ad.body ? <p className="text-sm ui-muted mb-2">{ad.body}</p> : null}

      {ad.format === 'Video' && ad.videoUrl ? (
        <video
          src={ad.videoUrl}
          className="w-full rounded-lg max-h-[70vh] object-cover"
          muted
          autoPlay
          loop
          playsInline
        />
      ) : ad.imageUrl ? (
        <img src={ad.imageUrl} alt={ad.title || 'ad'} className="w-full rounded-lg object-cover" />
      ) : null}

      <div className="mt-3">
        <span className="inline-flex items-center rounded-lg bg-[#0071A6] text-white text-sm font-medium px-4 py-2">
          {ad.ctaText || 'Tìm hiểu thêm'}
        </span>
      </div>
    </div>
  );
};

export default NativeAdCard;
